import type {IssueFormRef} from '@github-ui/issue-form/Types'
import {fetchTopRepositories, type RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import {noop} from '@github-ui/noop'
import {issuePath} from '@github-ui/paths'
import {isCancelledError, useQueryClient} from '@github-ui/react-query'
import {type RefObject, useCallback, useEffect, useRef, useState} from 'react'

import {ERRORS} from '../constants/errors'
import {VALUES} from '../constants/values'
import {IssueCreateContextProvider} from '../contexts/IssueCreateContext'
import type {CreateIssueFragment} from '../create-issue-fragment-types'
import type {CreateIssueProps} from '../CreateIssue'
import type {CreateIssueCallbackProps} from '../CreateIssueForm'
import type {TemplateListSharedProps} from '../TemplateList'
import {DisplayMode} from '../utils/display-mode'
import type {OnCreateProps} from '../utils/model'
import type {IssueCreateOptionConfig, SafeOptionConfig} from '../utils/option-config'
import {getSafeConfig} from '../utils/option-config'
import {constructIssueCreateParams} from '../utils/template-args'
import {CreateIssueDialog} from './CreateIssueDialog'
import {DIALOG_ENTRY_STALE_TIME, dialogEntryQueryKey, fetchDialogEntry, runLatestRequest} from './fetch-dialog-entry'

// The consumer boundary throughout issue-create is `CreateIssueFragment`; hold the fetched
// repository as that type directly so both fetch paths converge here without an `unknown` bridge.
type CurrentRepositoryData = {readonly repository: CreateIssueFragment | null}

export type CreateIssueDialogEntryProps = {
  navigate: (url: string) => void
  isCreateDialogOpen: boolean
  setIsCreateDialogOpen: (value: boolean) => void
  optionConfig?: IssueCreateOptionConfig
  returnFocusRef?: RefObject<HTMLElement | null>
  fallbackElement?: React.ReactElement
  requiresRelayEnvironment?: boolean
} & Partial<CreateIssueCallbackProps> &
  TemplateListSharedProps

export const CreateIssueDialogEntry = ({
  navigate,
  isCreateDialogOpen,
  setIsCreateDialogOpen,
  onCancel,
  onBeforeCreate,
  onCreateSuccess,
  onCreateError,
  onSubIssueOptimisticCreate,
  optionConfig,
  isNavigatingToNew,
  setIsNavigatingToNew,
  returnFocusRef,
  fallbackElement,
  requiresRelayEnvironment,
}: CreateIssueDialogEntryProps): React.ReactElement | null => {
  const queryClient = useQueryClient()
  const [topRepositories, setTopRepositories] = useState<RepositoryPickerRepository[] | null>(null)
  const [currentRepository, setCurrentRepository] = useState<CurrentRepositoryData | null>(null)
  // Guards against a slow earlier dialog-entry fetch clobbering a newer one (e.g. StrictMode
  // double-invoke or a quick close/reopen). Only the latest request applies its result.
  const latestDialogEntryRequestRef = useRef(0)
  // Advances the request generation so any in-flight dialog-entry request is treated as stale
  // (its captured id no longer matches), suppressing a late success/failure. Stable, so it can be
  // called from the mount effect's cleanup without tripping the react-hooks ref-cleanup guard.
  const invalidateLatestDialogEntryRequest = useCallback(() => {
    latestDialogEntryRequestRef.current++
  }, [])

  const config = getSafeConfig(optionConfig)
  const shouldLoadTopRepos = config.showRepositoryPicker
  const shouldLoadCurrentRepo = config.issueCreateArguments?.repository ? true : false

  useEffect(() => {
    const fetchTopRepos = async () => {
      try {
        const repos = await fetchTopRepositories({
          first: VALUES.repositoriesPreloadCount,
          hasIssuesEnabled: true,
        })
        setTopRepositories(repos)
      } catch (error) {
        if (isCancelledError(error)) return
        reportError(error instanceof Error ? error : new Error('Unable to load top repositories'))
        setTopRepositories([])
      }
    }

    const fetchCurrentRepo = async () => {
      const repository = config.issueCreateArguments?.repository
      if (!repository) return

      void runLatestRequest(
        latestDialogEntryRequestRef,
        () =>
          queryClient.fetchQuery({
            queryKey: dialogEntryQueryKey(repository.owner, repository.name),
            queryFn: ({signal}) => fetchDialogEntry(repository.owner, repository.name, signal),
            staleTime: DIALOG_ENTRY_STALE_TIME,
            retry: false,
          }),
        data => setCurrentRepository(data),
        () => {
          reportError(new Error(ERRORS.unableToLoadCurrentRepository))
          setCurrentRepository({repository: null})
        },
      )
    }

    if (shouldLoadCurrentRepo) {
      fetchCurrentRepo()
    }
    if (shouldLoadTopRepos) {
      void fetchTopRepos()
    }
    // Invalidate any in-flight dialog-entry request on unmount so a late success/failure can't
    // apply state or report an error after the dialog has gone.
    return invalidateLatestDialogEntryRequest
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleDialogClose = () => {
    onCancel?.()
    setIsCreateDialogOpen(false)
  }

  const handleOnCreate = ({issue, createMore}: OnCreateProps) => {
    // Respect the callback if it exists, else use default behaviour.
    if (onCreateSuccess) {
      onCreateSuccess({issue, createMore})
    } else if (!createMore) {
      handleDialogClose()
      navigate(
        issuePath({
          owner: issue.repository.owner.login,
          repo: issue.repository.name,
          issueNumber: issue.number,
        }),
      )
    }
  }

  const handleOnError = (error: Error) => onCreateError?.(error)

  const createIssueDialogProps: CreateIssuePropsEntry = {
    topRepositories: shouldLoadTopRepos ? topRepositories : 'unset',
    onCreateSuccess: handleOnCreate,
    onCreateError: handleOnError,
    onBeforeCreate,
    onSubIssueOptimisticCreate,
    onCancel: handleDialogClose,
    navigate,
    isNavigatingToNew,
    setIsNavigatingToNew,
    requiresRelayEnvironment,
  }

  if (!isCreateDialogOpen) {
    return null
  }

  return (
    <CreateIssueEntryWrapper
      createIssueProps={createIssueDialogProps}
      config={config}
      currentRepository={shouldLoadCurrentRepo ? currentRepository : 'unset'}
      returnFocusRef={returnFocusRef}
      fallbackElement={fallbackElement}
    />
  )
}

export type CreateIssuePropsEntry = Omit<CreateIssueProps, 'issueFormRef' | 'currentRepository'>

type CreateIssueEntryWrapperProps = {
  createIssueProps: CreateIssuePropsEntry
  config: SafeOptionConfig
  currentRepository: CurrentRepositoryData | null | 'unset'
  returnFocusRef?: RefObject<HTMLElement | null>
  fallbackElement?: React.ReactElement
}

const CreateIssueEntryWrapper = ({
  createIssueProps,
  config,
  currentRepository,
  returnFocusRef,
  fallbackElement,
}: CreateIssueEntryWrapperProps) => {
  const navigate = createIssueProps.navigate ?? noop
  const repository =
    currentRepository !== 'unset' && currentRepository && currentRepository.repository
      ? currentRepository.repository
      : null
  const issueFormRef = useRef<IssueFormRef>(null)

  const preselectedData = {
    repository: repository ?? undefined,
    template: undefined,
    parentIssue: config.issueCreateArguments?.parentIssue,
  }

  const props = {
    ...createIssueProps,
    issueFormRef,
    hideCreateMore: config.hideCreateMore,
  }

  let redirect = false
  const shouldEvaluateRedirect =
    repository?.hasIssuesEnabled && config.defaultDisplayMode !== DisplayMode.IssueDuplication
  if (shouldEvaluateRedirect) {
    // Redirect when no templates AND blank issues are enabled
    // hasAnyTemplates is always true if there are any contact links for the repo
    redirect = navigate !== noop && !repository.hasAnyTemplates && repository.isBlankIssuesEnabled
  }

  useEffect(() => {
    if (redirect && repository) {
      const searchParams = constructIssueCreateParams({
        includeRepository: false,
        repository,
        templateFileName: undefined,
        ...config.issueCreateArguments?.initialValues,
      })
      const url = `/${repository.nameWithOwner}/issues/new${searchParams ? `?${searchParams}` : ''}`
      navigate(url)
    }
  }, [navigate, redirect, repository, config.issueCreateArguments?.initialValues])

  if (redirect) {
    return null
  }

  // If we have a fallback element and data is still loading, render the fallback
  const isLoadingCurrentRepo = currentRepository === 'unset' ? false : currentRepository === null
  const isLoadingTopRepos =
    createIssueProps.topRepositories === 'unset' ? false : createIssueProps.topRepositories === null
  const isLoading = isLoadingCurrentRepo || isLoadingTopRepos
  if (isLoading) {
    return fallbackElement ? fallbackElement : null
  }

  return (
    <IssueCreateContextProvider
      optionConfig={config}
      overrideFallbackDisplaymode={DisplayMode.TemplatePicker}
      preselectedData={preselectedData}
    >
      <CreateIssueDialog
        currentRepository={currentRepository === 'unset' ? 'unset' : (currentRepository?.repository ?? null)}
        {...props}
        returnFocusRef={returnFocusRef}
      />
    </IssueCreateContextProvider>
  )
}
